import { toast } from "react-toastify";
import { reportUnexpectedError } from "../../../error-reporter";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "../../../graphql/client";
import {
  gqlClient as smartApplyGqlClient,
  runGqlQueryV2 as smartApplyRunGqlQueryV2,
  GrafferGqlResult,
} from "@graffer-inc/smart-apply";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import {
  OrgProductType,
  PlanNameMap,
  ProductNameMap,
} from "../../domain/organization/org-product";

export type OrgProductsContextProps = {
  orgProducts: OrgProductType[];
  procedurePublishingCount: GrafferGqlResult<number>;
  applicationCount: GrafferGqlResult<number>;
  surveyPublishingCount: GrafferGqlResult<number>;
  surveyAnswerCount: GrafferGqlResult<number>;
  fetchOrgProduct: typeof OrgProductsService.prototype.fetchOrgProduct;
  updateOrgProduct: typeof OrgProductsService.prototype.updateOrgProduct;
  createOrgProduct: typeof OrgProductsService.prototype.createOrgProduct;
  fetchTenantDataCount: typeof OrgProductsService.prototype.fetchTenantDataCount;
};

export class OrgProductsService extends BaseApplicationService<OrgProductsContextProps> {
  state = {
    orgProducts: [],
    procedurePublishingCount: GrafferGqlResult.init(),
    applicationCount: GrafferGqlResult.init(),
    surveyPublishingCount: GrafferGqlResult.init(),
    surveyAnswerCount: GrafferGqlResult.init(),
    fetchOrgProduct: this.fetchOrgProduct.bind(this),
    updateOrgProduct: this.updateOrgProduct.bind(this),
    createOrgProduct: this.createOrgProduct.bind(this),
    fetchTenantDataCount: this.fetchTenantDataCount.bind(this),
  };

  async fetchOrgProduct(orgId: string) {
    const res = await runGqlQueryV2(() =>
      gqlClient.allOrgProductsByOrgId({ orgId }, gqlHeader("q")),
    );
    if (res.error) {
      toast.error("製品情報の取得に失敗しました。");
      return;
    }
    const orgProducts: OrgProductType[] = res.data!.allOrgProductsByOrgId.map(
      (v) => ({
        id: v.id,
        orgId: v.orgId,
        productCode: v.productCode,
        productName: ProductNameMap[v.productCode],
        plan: v.plan,
        planName: PlanNameMap[v.plan],
        legalTermUrl: v.legalTermUrl,
      }),
    );
    this.setState({
      orgProducts: orgProducts,
    });
  }

  async updateOrgProduct(orgProduct: OrgProductType): Promise<boolean> {
    const res = await runGqlQueryV2(() =>
      gqlClient.UpdateOrgProduct(
        {
          orgId: orgProduct.orgId,
          productCode: orgProduct.productCode,
          plan: orgProduct.plan,
          legalTermUrl: orgProduct.legalTermUrl,
        },
        gqlHeader("m"),
      ),
    );
    if (res.error) {
      reportUnexpectedError(res.error.rawError);
      toast.error("製品プランの変更に失敗しました。");
      return false;
    }
    return true;
  }

  async createOrgProduct(orgProduct: OrgProductType): Promise<boolean> {
    const res = await runGqlQueryV2(() =>
      gqlClient.CreateOrgProduct(
        {
          orgId: orgProduct.orgId,
          productCode: orgProduct.productCode,
          plan: orgProduct.plan,
          legalTermUrl: orgProduct.legalTermUrl,
        },
        gqlHeader("m"),
      ),
    );
    if (res.error) {
      reportUnexpectedError(res.error.rawError);
      toast.error("製品プランの追加に失敗しました。");
      return false;
    }
    return true;
  }

  async fetchTenantDataCount(orgId: string) {
    const res = await smartApplyRunGqlQueryV2(() =>
      smartApplyGqlClient.tenantDataCount({ id: orgId }, gqlHeader("q")),
    );
    this.setState({
      procedurePublishingCount: res.convert((d) => d.publishingProcedureCount),
      applicationCount: res.convert((d) => d.applicationCountByOrgId),
      surveyPublishingCount: res.convert((d) => d.publishingSurveyCount),
      surveyAnswerCount: res.convert((d) => d.surveyAnswerCountByOrgId),
    });
  }
}
